import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  Clock,
  EllipsisVertical,
  ExternalLink,
  Globe,
  KeyRound,
  RefreshCw,
  Unplug
} from 'lucide-react'
import type { JobDTO, SiteDTO, SshKeyDTO } from '@shared/types'
import { Role, isJobActive } from '@shared/types'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatNumber, relativeTime } from '@/lib/format'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  DropdownMenu,
  IconButton,
  Modal,
  Select,
  useToast
} from '@/components/ui'
import { JobProgressBar } from '@/components/media/JobProgressBar'
import { useAuthStore } from '@/stores/useAuthStore'
import { useJobStore } from '@/stores/useJobStore'
import { useSitesStore } from '@/stores/useSitesStore'

export interface SiteCardProps {
  site: SiteDTO
  /** Needed by the "Change SSH key" action; the page owns the fetch. */
  sshKeys: SshKeyDTO[]
  onDelete: (site: SiteDTO) => void
}

interface StatProps {
  label: string
  value: number
  tone?: 'default' | 'warn' | 'accent' | 'ok'
}

const statTones: Record<NonNullable<StatProps['tone']>, string> = {
  default: 'text-fg',
  warn: 'text-warn-700 dark:text-warn-300',
  accent: 'text-accent',
  ok: 'text-ok-700 dark:text-ok-300'
}

function Stat({ label, value, tone = 'default' }: StatProps): React.ReactElement {
  return (
    <div className="rounded-control border border-line bg-sunken px-2.5 py-2">
      <dt className="text-[10px] leading-tight font-semibold tracking-[0.08em] text-fg-subtle uppercase">
        {label}
      </dt>
      <dd className={cn('mt-1 text-base leading-none font-semibold tabular-nums', statTones[tone])}>
        {formatNumber(value)}
      </dd>
    </div>
  )
}

function displayUrl(siteUrl: string): string {
  return siteUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '')
}

/**
 * One install connected in this session. "Saved" is the only column that means
 * anything after the session ends — everything else on this card is deleted with
 * it, which is why the destructive action is Disconnect rather than Delete.
 */
export default function SiteCard({ site, sshKeys, onDelete }: SiteCardProps): React.ReactElement {
  const navigate = useNavigate()
  const { toast } = useToast()
  const update = useSitesStore((state) => state.update)

  // Prefer the live job for this site so a stale `activeJobId` on the DTO never
  // pins a finished job to the card.
  const job = useJobStore(
    (state) =>
      state.activeForSite(site.id) ??
      (site.activeJobId ? (state.jobs[site.activeJobId] ?? null) : null)
  )

  // One WP Engine key serves the whole instance, so a regular user has exactly one
  // to choose from and nothing to decide. Showing them a "Change SSH key" action is
  // clutter that invites questions; admins keep it for repointing a site.
  const isAdmin = useAuthStore((state) => state.user?.role) === Role.Admin

  const [starting, setStarting] = useState(false)
  const [keyModalOpen, setKeyModalOpen] = useState(false)
  const [selectedKeyId, setSelectedKeyId] = useState(site.sshKeyId ?? '')
  const [savingKey, setSavingKey] = useState(false)

  useEffect(() => {
    if (keyModalOpen) setSelectedKeyId(site.sshKeyId ?? sshKeys[0]?.id ?? '')
  }, [keyModalOpen, site.sshKeyId, sshKeys])

  const liveJob = job !== null && isJobActive(job) ? job : null
  const jobActive = liveJob !== null || site.activeJobId !== null
  const busy = jobActive || starting

  const handleRefetch = async (): Promise<void> => {
    if (busy) return
    setStarting(true)
    try {
      const started = await api.post<JobDTO>(`/sites/${site.id}/fetch`)
      useJobStore.getState().applyEvent({ type: 'job', job: started })
      useSitesStore.getState().upsertFromEvent(site.id, { activeJobId: started.id })
      toast({
        title: 'Refetching media',
        description: `Argus is re-reading the media library for ${site.installName}.`,
        variant: 'info'
      })
    } catch (cause) {
      toast({
        title: 'Could not start the refetch',
        description: errorMessage(cause),
        variant: 'error'
      })
    } finally {
      setStarting(false)
    }
  }

  const handleCancel = async (): Promise<void> => {
    if (!liveJob) return
    try {
      await useJobStore.getState().cancel(liveJob.id)
    } catch (cause) {
      toast({ title: 'Could not cancel', description: errorMessage(cause), variant: 'error' })
    }
  }

  const handleKeyChange = async (): Promise<void> => {
    if (!selectedKeyId || selectedKeyId === site.sshKeyId) {
      setKeyModalOpen(false)
      return
    }
    setSavingKey(true)
    try {
      await update(site.id, { sshKeyId: selectedKeyId })
      setKeyModalOpen(false)
      toast({ title: 'SSH key updated', variant: 'success' })
    } catch (cause) {
      toast({
        title: 'Could not change the key',
        description: errorMessage(cause),
        variant: 'error'
      })
    } finally {
      setSavingKey(false)
    }
  }

  const url = displayUrl(site.siteUrl)

  return (
    <Card className="@container flex w-full flex-col">
      <CardBody className="flex-1">
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="grid size-10 shrink-0 place-items-center rounded-control bg-accent-soft text-accent"
          >
            <Globe className="size-5" />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <h3 className="min-w-0 truncate text-[15px] leading-tight font-semibold text-fg">
                <Link
                  to={`/sites/${site.id}`}
                  className="rounded-sm transition-colors hover:text-accent"
                >
                  {site.installName}
                </Link>
              </h3>
              {site.wpVersion ? (
                <Badge size="sm" variant="neutral" className="shrink-0">
                  WP {site.wpVersion}
                </Badge>
              ) : null}
            </div>

            {url ? (
              <a
                href={site.siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 flex min-w-0 items-center gap-1 text-xs text-fg-muted transition-colors hover:text-accent"
              >
                <span className="truncate">{url}</span>
                <ExternalLink className="size-3 shrink-0" aria-hidden="true" />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            ) : (
              <p className="mt-1 text-xs text-fg-subtle">Site URL not discovered yet</p>
            )}
          </div>

          <DropdownMenu
            trigger={
              <IconButton label={`Actions for ${site.installName}`} size="sm">
                <EllipsisVertical />
              </IconButton>
            }
            items={[
              {
                label: 'Refetch images',
                icon: <RefreshCw />,
                disabled: busy,
                onSelect: () => void handleRefetch()
              },
              ...(isAdmin
                ? [
                    {
                      label: 'Change SSH key',
                      icon: <KeyRound />,
                      onSelect: () => setKeyModalOpen(true)
                    }
                  ]
                : []),
              { separator: true },
              {
                label: 'Disconnect site',
                icon: <Unplug />,
                danger: true,
                onSelect: () => onDelete(site)
              }
            ]}
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fg-muted">
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <KeyRound className="size-3.5 shrink-0 text-fg-subtle" aria-hidden="true" />
            <span className="truncate">{site.sshKeyLabel ?? 'No SSH key assigned'}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="size-3.5 shrink-0 text-fg-subtle" aria-hidden="true" />
            {site.lastFetchedAt
              ? `Fetched ${relativeTime(site.lastFetchedAt)}`
              : 'No media fetched yet'}
          </span>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-2 @sm:grid-cols-4">
          <Stat label="Total" value={site.imageCount} />
          <Stat
            label="Missing alt"
            value={site.missingAltCount}
            tone={site.missingAltCount > 0 ? 'warn' : 'default'}
          />
          <Stat
            label="Approved"
            value={site.approvedCount}
            tone={site.approvedCount > 0 ? 'accent' : 'default'}
          />
          <Stat
            label="Saved"
            value={site.savedCount}
            tone={site.savedCount > 0 ? 'ok' : 'default'}
          />
        </dl>

        {liveJob ? (
          // The shared bar so a queued generate reads as "waiting for a slot" here
          // too, rather than as an indeterminate bar that looks like work started.
          <div className="mt-4">
            <JobProgressBar
              job={liveJob}
              onCancel={() => void handleCancel()}
              cancelling={liveJob.cancelRequested}
            />
          </div>
        ) : jobActive ? (
          <p aria-live="polite" className="mt-4 text-xs text-fg-muted">
            A job is running for this install — opening it shows the progress.
          </p>
        ) : null}
      </CardBody>

      <CardFooter>
        <Button
          fullWidth
          size="sm"
          iconRight={<ArrowRight />}
          onClick={() => navigate(`/sites/${site.id}`)}
        >
          Open media library
        </Button>
      </CardFooter>

      <Modal
        open={keyModalOpen}
        onClose={() => {
          if (!savingKey) setKeyModalOpen(false)
        }}
        title="Change SSH key"
        description={`Choose the key Argus uses to reach ${site.installName}.`}
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              disabled={savingKey}
              onClick={() => setKeyModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              loading={savingKey}
              disabled={sshKeys.length === 0}
              onClick={() => void handleKeyChange()}
            >
              Save key
            </Button>
          </>
        }
      >
        {sshKeys.length === 0 ? (
          <p className="text-sm leading-relaxed text-fg-muted">
            No WP Engine key is configured for this instance yet.{' '}
            <Link
              to="/keys"
              className="font-medium text-accent underline decoration-transparent underline-offset-2 transition-colors hover:decoration-current"
            >
              Set one up on the SSH keys page
            </Link>{' '}
            and come back.
          </p>
        ) : (
          <Select
            label="SSH key"
            value={selectedKeyId}
            onChange={(event) => setSelectedKeyId(event.target.value)}
            options={sshKeys.map((key) => ({
              value: key.id,
              label: `${key.label} · ${key.keyType}`
            }))}
            hint="The new key is used the next time Argus connects to this install. Keys are stored on your account and outlive this session."
          />
        )}
      </Modal>
    </Card>
  )
}

export { SiteCard }
